import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { includedPerLight, includedPerLightEs, pricingConfig, warrantyConfig } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatMoney, priceIndividual } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { QuantityStepper } from '../ui/QuantityStepper';
import { WarrantyTag } from '../ui/WarrantyTag';
import { ProductAssetSlot } from '../product/ProductAssetSlot';
import { ProductRating } from '../reviews/ProductRating';
const QUICK_PICKS = [1, 2, 3, 4, 6, 8, 10, 12, 15, 20];
export function BuildYourOwn({
  initialQty = 1
}: {
  initialQty?: number;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    startIndividual
  } = useFunnel();
  const [qty, setQty] = useState(initialQty);
  const p = priceIndividual(qty);
  const m = (n: number) => formatMoney(n, lang);
  const included = lang === 'es' ? includedPerLightEs : includedPerLight;
  return <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16" data-id="element-713" data-mp="2sefadbjnh98x">
      <div data-id="element-714" data-mp="2xcqhvfmh37nz">
        <ProductAssetSlot view="front" tone="studio" className="aspect-[4/3] w-full" data-id="element-715" data-mp="2x92p4iouqpab" />
        <div className="mt-10" data-id="element-716" data-mp="2ifprbfg0pir3">
          <label className="block font-display text-lg font-semibold uppercase tracking-[0.08em]" htmlFor="byo-qty" data-id="element-717" data-mp="2nczqvnx4fasn">
            How many M1 PRO lights?
          </label>
          <div className="mt-4" id="byo-qty" data-id="element-718" data-mp="3b3c3vd783xq3">
            <QuantityStepper value={qty} onChange={setQty} min={1} max={pricingConfig.maxIndividualQty} label="number of lights" size="lg" data-id="element-719" data-mp="3g26l4apjle83" />
          </div>
          <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Quick quantity" data-id="element-720" data-mp="36a0yzhlz1r0t">
            {QUICK_PICKS.map(n => <button key={n} type="button" aria-pressed={qty === n} onClick={() => setQty(n)} className={`h-10 min-w-[48px] rounded-md border px-3 text-sm font-semibold transition-colors duration-150 ${qty === n ? 'border-marine bg-marine text-white' : 'mf-hairline text-titanium hover:text-white'}`} data-id="element-721" data-mp="2mh2k262qle4">
                {n}
              </button>)}
          </div>
        </div>

        <p className="mt-8 text-sm text-titanium" data-id="element-722" data-mp="3ppixdqp0gibd">
          Each M1 PRO is {m(pricingConfig.unitPrice)}. Lighting 2, 4, 6, 8, 12 or 24? A complete system costs less than the same
          number of individual lights.
        </p>
      </div>

      <aside aria-label="Your system summary" className="lg:sticky lg:top-32 lg:self-start" data-id="element-723" data-mp="3fzpqv43q6tdz">
        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-8" data-id="element-724" data-mp="3bzp8j8di9y1n">
          <ProductRating data-id="element-725" data-mp="36xgee8owcpxy" />
          <h3 className="mt-3 font-display text-2xl font-semibold uppercase tracking-[0.04em]" data-id="element-726" data-mp="370uh5i1h315x">Your system</h3>
          <dl className="mt-6 space-y-3 text-sm" data-id="element-727" data-mp="32212vned364p">
            <Row label="Selected quantity" value={`${qty} ${qty === 1 ? t('common.light') : t('common.lights')}`} data-id="element-728" data-mp="1hgkvehs2ai8c" />
            <Row label="Unit price" value={m(p.unit)} data-id="element-729" data-mp="1mf5uuezhoxvq" />
          </dl>
          <div className="mt-6 flex items-baseline justify-between border-t mf-hairline pt-5" data-id="element-730" data-mp="1rfk4g1gqofvv">
            <span className="text-titanium" data-id="element-731" data-mp="1vhx6ofwa1vnw">{t('common.total')}</span>
            <span className="font-display text-5xl font-semibold" data-id="element-732" data-mp="yr4s5mozsbo8">{m(p.total)}</span>
          </div>

          <div className="mt-6 space-y-3 border-t mf-hairline pt-5 text-sm text-titanium" data-id="element-733" data-mp="13q90yc8087e1">
            <WarrantyTag tier={warrantyConfig.individual} data-id="element-734" data-mp="13nv5w3dbqv6h" />
            <p data-id="element-735" data-mp="17r1cjc4cmodh">{t('common.shipping')}.</p>
            <ul className="space-y-2" data-id="element-736" data-mp="17q0136jf7zv8">
              {included.map(i => <li key={i} className="flex gap-2" data-id="element-737" data-mp="1cjuig3bk02r5">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-738" data-mp="g1gd731jd5ui" />
                  {qty} × {i}
                </li>)}
            </ul>
          </div>

          <button type="button" onClick={() => startIndividual(qty)} className={`${buttonClasses('primary', 'lg')} mt-8 w-full`} data-id="element-739" data-mp="x3s673mhr9k">
            {t('common.addToCart')} — {m(p.total)}
          </button>
        </div>
      </aside>
    </div>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex justify-between gap-4" data-id="element-740" data-mp="5uvkv3c3dhet">
      <dt className="text-titanium" data-id="element-741" data-mp="jn3eim82ws9a">{label}</dt>
      <dd className="text-right text-white" data-id="element-742" data-mp="jpxxsafcw714">{value}</dd>
    </div>;
}